import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';

import { AlertService } from '@core/services';
import { Alert, AlertType } from '@core/models';
import { fadeInOut } from '@core/animations';

const alertTypeClass = {
  [AlertType.Banner]: 'success',
  [AlertType.Error]: 'danger',
  [AlertType.Info]: 'info',
  [AlertType.Success]: 'success',
  [AlertType.Warning]: 'warning',
};

@Component({
  selector: 'sp-alert',
  templateUrl: 'alert.component.html',
  styleUrls: ['./alert.component.scss'],
  animations: [fadeInOut],
})
export class AlertComponent implements OnInit, OnDestroy {
  id = 'default-alert';
  fade = true;
  alerts: Alert[] = [];

  //* Subscriptions
  alertSubscription: Subscription;
  routeSubscription: Subscription;

  constructor(private router: Router, private alertService: AlertService) {}

  ngOnInit() {
    //* subscribe to new alert notifications
    this.alertSubscription = this.newAlertNoticiation();

    //* clear alerts on location change
    this.routeSubscription = this.removeRouteSubscription();

    this.alertService.clearAlert$.subscribe(() => this.clearAllAlerts());
  }

  ngOnDestroy() {
    //* no memory leaks, please.
    this.alertSubscription.unsubscribe();
    this.routeSubscription.unsubscribe();
  }

  newAlertNoticiation(): Subscription {
    return this.alertService.onAlert(this.id).subscribe(alert => {
      //* clear alerts when an empty alert is received
      if (!alert.message) return;

      //* add alert to array
      if (!this.alerts.some(a => a.uniqueId === alert.uniqueId)) {
        this.alerts.push(alert);
      }

      //* auto close alert with fade in 3 secs.
      if (alert.autoDisappear) {
        setTimeout(() => this.removeAlert(alert), 3000);
      }
    });
  }

  removeRouteSubscription(): Subscription {
    return this.router.events.subscribe(event => {
      //* if page changes, clear.
      if (event instanceof NavigationStart) {
        this.alertService.clear(this.id);
      }
    });
  }

  removeAlert(alert: Alert): void {
    const filterAlert = (alert: Alert) => this.alerts.filter(currAlert => currAlert !== alert);

    const fadeOut = (alert: Alert): void => {
      //* find alert with fade property.
      this.alerts.find(currAlert => currAlert === alert).fade = true;

      //* remove alert after faded out.
      setTimeout(() => {
        this.alerts = filterAlert(alert);
      }, 250);
    };

    //* check if alert already removed.
    if (!this.alerts.includes(alert)) return;
    //* remove alert.
    if (this.fade) return fadeOut(alert);
    //* remove alert
    else this.alerts = filterAlert(alert);
  }

  alertContainerClass(alert: Alert): string {
    if (!alert) return;

    const isFade = (alert: Alert) => alert.fade && 'fade';

    return [isFade(alert), `alert-type-${alertTypeClass[alert.type]}`].join(' ');
  }

  alertIconClass(alert: Alert): string {
    if (!alert) return;

    return [`${alert.icon}`, `alert-type-icon-${alertTypeClass[alert.type]}`].join(' ');
  }

  clearAllAlerts() {
    this.alerts = [];
  }
}
